// ── Strategy Designer v2 — AI-first, code editor, flowchart canvas ────
// Layout: GQ Chat (left) | Flowchart Canvas (center) | Code/Params (right)

const INDICATOR_PALETTE = [
  { group:"Moving Averages", items:["SMA","EMA","TEMA","DEMA","WMA","HMA","VWAP"] },
  { group:"Oscillators",     items:["RSI","MACD","Stochastic","CCI","Williams %R"] },
  { group:"Volume",          items:["OBV","Volume Profile","MFI","Accumulation"] },
  { group:"Volatility",      items:["Bollinger","ATR","Keltner","Donchian"] },
  { group:"Logic",           items:["AND","OR","NOT","Crossover","Threshold","Time Filter"] },
];

const NODE_COLORS = {SMA:"#3b9eff",EMA:"#3b9eff",TEMA:"#3b9eff",DEMA:"#3b9eff",WMA:"#3b9eff",HMA:"#3b9eff",VWAP:"#3b9eff",RSI:"#11ff99",MACD:"#11ff99",Stochastic:"#11ff99",CCI:"#11ff99","Williams %R":"#11ff99",OBV:"#8b5cf6",MFI:"#8b5cf6","Volume Profile":"#8b5cf6",Accumulation:"#8b5cf6",Bollinger:"#D946EF",ATR:"#D946EF",Keltner:"#D946EF",Donchian:"#D946EF",AND:"#11ff99",OR:"#11ff99",NOT:"#f87171",Crossover:"#3b9eff",Threshold:"#3b9eff","Time Filter":"#8b5cf6"};

const INIT_NODES = [
  {id:"n1",type:"EMA",label:"EMA(9)",x:40,y:50,params:{Period:9,Source:"Close"}},
  {id:"n2",type:"EMA",label:"EMA(21)",x:40,y:140,params:{Period:21,Source:"Close"}},
  {id:"n3",type:"Crossover",label:"Crossover",x:240,y:95,params:{Type:"Bullish"}},
  {id:"n4",type:"RSI",label:"RSI(14)",x:40,y:240,params:{Period:14,Overbought:70,Oversold:30}},
  {id:"n5",type:"Threshold",label:"RSI > 50",x:240,y:240,params:{Op:">",Value:50}},
  {id:"n6",type:"AND",label:"Entry signal",x:440,y:165,params:{}},
];
const INIT_EDGES = [{from:"n1",to:"n3"},{from:"n2",to:"n3"},{from:"n3",to:"n6"},{from:"n4",to:"n5"},{from:"n5",to:"n6"}];

const DEMO_CODE = `// NASDAQ Momentum Strategy
const strategy = await gq.create({
  name: "NASDAQ Momentum",
  indicators: ["EMA(9)", "EMA(21)", "RSI(14)"],
  entry: "crossover AND rsi > 50",
  exit: "crossunder OR rsi < 30",
  capital: 10000,
  stopLoss: 0.035,
  takeProfit: 0.08
});

// Backtest config
strategy.backtest({
  from: "2020-01-01",
  to: "2024-12-31",
  fees: 0.05,
  slippage: 0.02
});`;

const AI_PROMPTS = [
  "Build a BTC momentum strategy with RSI filter",
  "Add a stop loss at 3.5%",
  "Switch to 4-hour bars",
  "Add Bollinger Band squeeze entry",
  "Optimize for Sharpe ratio",
];

// ── Flowchart node ────────────────────────────────────────────────────
function FlowNode({node, selected, onClick}) {
  const c = NODE_COLORS[node.type] || "#D946EF";
  return (
    <g onClick={()=>onClick(node)} style={{cursor:"pointer"}}>
      <rect x={node.x} y={node.y} width={140} height={44} rx={8}
        fill="var(--bg-card)" stroke={selected?c:"rgba(255,255,255,0.1)"} strokeWidth={selected?1.5:1}/>
      <circle cx={node.x+12} cy={node.y+14} r={3} fill={c}/>
      <text x={node.x+20} y={node.y+16} fontFamily="var(--font-ui)" fontSize={9} fill="rgba(245,245,247,0.5)" fontWeight={500}>{node.type}</text>
      <text x={node.x+12} y={node.y+33} fontFamily="var(--font-mono)" fontSize={11} fill="var(--ink)">{node.label}</text>
      {/* Input port */}
      <circle cx={node.x} cy={node.y+22} r={3.5} fill="var(--bg)" stroke="rgba(255,255,255,0.15)" strokeWidth={1}/>
      {/* Output port */}
      <circle cx={node.x+140} cy={node.y+22} r={3.5} fill={c} opacity={0.6}/>
    </g>
  );
}

// ── GQ Chat message ───────────────────────────────────────────────────
function ChatMsg({role, text, thinking}) {
  const isAI = role === "ai";
  return (
    <div style={{display:"flex",gap:8,marginBottom:12,alignItems:"flex-start"}}>
      {isAI && <div style={{width:24,height:24,borderRadius:6,background:"var(--primary-dim)",border:"1px solid rgba(217,70,239,0.3)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:2}}>
        <span style={{fontFamily:"'Inter Tight','Inter',sans-serif",fontSize:9,fontWeight:800,color:"#D946EF"}}>GQ</span>
      </div>}
      <div style={{flex:1,textAlign:isAI?"left":"right"}}>
        <div style={{
          display:"inline-block",maxWidth:"95%",padding:"8px 12px",borderRadius:10,
          background:isAI?"rgba(255,255,255,0.03)":"rgba(59,158,255,0.08)",
          border:`1px solid ${isAI?"var(--border)":"rgba(59,158,255,0.15)"}`,
          fontFamily:"var(--font-ui)",fontSize:12,color:isAI?"var(--body)":"var(--ink)",lineHeight:1.55,textAlign:"left",
        }}>
          {thinking && <span style={{color:"var(--muted)",fontStyle:"italic"}}>Thinking…</span>}
          {!thinking && text}
        </div>
      </div>
    </div>
  );
}

// ── Main Designer Page ────────────────────────────────────────────────
function DesignerPage({onToast, onNavigate}) {
  const narrow = window.useNarrow ? window.useNarrow() : false;
  const [nodes, setNodes] = React.useState(INIT_NODES);
  const [edges] = React.useState(INIT_EDGES);
  const [selected, setSelected] = React.useState(null);
  const [code, setCode] = React.useState(DEMO_CODE);
  const [rightTab, setRightTab] = React.useState("code"); // code | params | import
  const [showBT, setShowBT] = React.useState(false);
  const [running, setRunning] = React.useState(false);
  const [chatInput, setChatInput] = React.useState("");
  const [messages, setMessages] = React.useState([
    {role:"ai",text:"Welcome to Strategy Lab. Describe what you want to build, or paste code and I'll parse it into the visual canvas."},
  ]);
  const [aiThinking, setAiThinking] = React.useState(false);
  const chatRef = React.useRef(null);
  const sel = nodes.find(n=>n.id===selected);

  // Scroll chat to bottom
  React.useEffect(()=>{if(chatRef.current)chatRef.current.scrollTop=chatRef.current.scrollHeight;},[messages]);

  // Real backtest result (live numbers, not mock)
  const [btResult, setBtResult] = React.useState(null);

  // ── Real AI chat: wired to window.claude.complete with strategy context
  const sendChat = async () => {
    if (!chatInput.trim() || aiThinking) return;
    const userMsg = chatInput.trim();
    setChatInput("");
    setMessages(p => [...p, { role:"user", text:userMsg }]);
    setAiThinking(true);

    const sysCtx = window.GQ?.state?.serialize ? window.GQ.state.serialize() : {};
    const sysPrompt = `You are GQ AI inside Strategy Lab. The user is building a quant strategy with a visual canvas + JS code.
Current canvas: ${nodes.length} nodes (${nodes.map(n => n.type).join(', ')}).
Current code:
\`\`\`js
${code.slice(0, 600)}
\`\`\`
Pipeline context: ${JSON.stringify(sysCtx).slice(0, 400)}

The user said: "${userMsg}"

Respond in under 100 words. If you want to add an indicator to the canvas, include a tag like <add type="RSI" label="RSI(14)" period="14"/> or <add type="EMA" period="50"/>. Multiple tags allowed. Be a quant: reference Sharpe, drawdown, regime when relevant.`;

    let response = '';
    try {
      response = await window.claude.complete(sysPrompt);
    } catch (e) {
      response = "AI service unavailable. Try again.";
    }

    // Parse <add .../> tags and update canvas
    const addRe = /<add\s+([^/]+)\/?>/g;
    const newNodes = []; let m; const baseId = Date.now();
    while ((m = addRe.exec(response)) !== null) {
      const attrs = {}; const a = /(\w+)="([^"]+)"/g; let mm;
      while ((mm = a.exec(m[1])) !== null) attrs[mm[1]] = mm[2];
      if (attrs.type) {
        newNodes.push({
          id: `n${baseId}_${newNodes.length}`,
          type: attrs.type,
          label: attrs.label || `${attrs.type}(${attrs.period || ''})`,
          x: 40 + Math.random() * 80, y: 300 + newNodes.length * 50,
          params: Object.fromEntries(Object.entries(attrs).filter(([k]) => !['type','label'].includes(k))),
        });
      }
    }
    if (newNodes.length) setNodes(p => [...p, ...newNodes]);
    const displayText = response.replace(/<add[^>]*\/?>/g, '').trim();
    setMessages(p => [...p, { role:"ai", text:displayText || "Canvas updated." }]);
    setAiThinking(false);
  };

  // ── Real backtest: builds strategy spec from nodes/code, runs engine, persists run
  const runBT = async () => {
    setRunning(true); setShowBT(false);
    onToast?.("Running backtest…");

    try {
      const { engine, data, state } = window.GQ;
      const bars = await data.load('SPY', { bars: 1260 });
      // Translate canvas nodes → engine strategy spec
      const indicators = []; const entryClauses = []; const exitClauses = [];
      indicators.push({ id:'atr14', type:'atr', source:'close', params:{period:14} });
      nodes.forEach((n, i) => {
        const id = (n.type.toLowerCase() + '_' + i).replace(/[^a-z0-9_]/g, '');
        if (n.type === 'EMA')      { indicators.push({ id, type:'ema', source:'close', params:{period:+n.params.Period || 21} }); }
        else if (n.type === 'SMA') { indicators.push({ id, type:'sma', source:'close', params:{period:+n.params.Period || 20} }); }
        else if (n.type === 'RSI') {
          indicators.push({ id, type:'rsi', source:'close', params:{period:+n.params.Period || 14} });
          entryClauses.push(`${id} > ${+n.params.Oversold || 50}`);
        }
        else if (n.type === 'Bollinger') { indicators.push({ id, type:'bbands', source:'close', params:{period:+n.params.Period || 20, k:+n.params.StdDev || 2} }); }
      });
      // EMA crossover convention: first two EMAs become "fast > slow"
      const emas = indicators.filter(s => s.type === 'ema').slice(0, 2);
      if (emas.length === 2) {
        entryClauses.unshift(`${emas[0].id} > ${emas[1].id}`);
        exitClauses.push(`${emas[0].id} < ${emas[1].id}`);
      }
      const strategy = {
        indicators,
        entry: { long: entryClauses.length ? [entryClauses.join(' and ')] : ['atr14 > 0'] },
        exit:  { long: exitClauses.length  ? exitClauses : ['atr14 < 0'] },
        sizing: { mode:'atrTarget', atrK:2, riskPct:0.01 },
        execution: { orderType:'market' },
        costs: { commissionBps:5, slippageBps:2, slippageModel:'volImpact' },
        constraints: { allowShort:false },
      };

      const result = window.GQ.engine.run(strategy, bars, { capital:10000, seed:42 });

      // Persist to state + Supabase
      const strat = state.getCurrentStrategy();
      if (strat?.id) {
        const savedRun = await state.saveBacktestRun({
          strategy_id: strat.id,
          asset: 'SPY', timeframe: '1d',
          date_start: bars[0].time, date_end: bars[bars.length - 1].time,
          capital: 10000, fees_bps: 5, slippage_bps: 2,
          config: strategy,
          metrics: result.metrics,
          trades: result.trades,
          equity_curve: result.equity_curve,
        });
        if (savedRun?.id) result.runId = savedRun.id;
      }
      setBtResult(result);
      setShowBT(true);
      onToast?.(`Backtest complete · Sharpe ${(result.metrics.sharpe || 0).toFixed(2)}`);
    } catch (e) {
      console.error('[Designer.runBT]', e);
      onToast?.("Backtest failed: " + e.message);
    } finally {
      setRunning(false);
    }
  };

  // Derive display stats from live result (with sensible fallback)
  const btStats = btResult ? {
    ret:     (btResult.metrics.totalReturn  * 100).toFixed(1),
    cagr:    (btResult.metrics.cagr         * 100).toFixed(1),
    sharpe:  +(btResult.metrics.sharpe      || 0).toFixed(2),
    sortino: +(btResult.metrics.sortino     || 0).toFixed(2),
    maxDD:   -((btResult.metrics.maxDrawdown * 100) || 0).toFixed(1),
    winRate: +(btResult.metrics.winRate * 100 || 0).toFixed(1),
    trades:  btResult.trades.length,
    pf:      +(btResult.metrics.profitFactor || 0).toFixed(2),
  } : { ret:0, cagr:0, sharpe:0, sortino:0, maxDD:0, winRate:0, trades:0, pf:0 };

  // Equity sparkline data
  const eqData = btResult
    ? btResult.equity_curve.filter((_, i) => i % Math.max(1, Math.floor(btResult.equity_curve.length / 60)) === 0).map(p => p.v)
    : Array.from({length:60}, (_, i) => 100 + i);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <PageHdr icon={Layers} iconColor="var(--accent)" iconBg="var(--accent-dim)"
        title="Strategy lab"
        subtitle="AI-powered strategy design, code, and backtest — all in one place."
        action={
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            <button className="btn btn-ghost btn-sm" onClick={()=>onToast?.("Saved")}><Check size={11}/> Save</button>
            <button className="btn btn-primary btn-sm" onClick={running?undefined:runBT} style={{opacity:running?0.7:1}}>
              {running?<><RefreshCw size={11} style={{animation:"spin 1s linear infinite"}}/> Running…</>:<><Play size={11}/> Run backtest</>}
            </button>
          </div>
        }
      />

      <div style={{display:"grid",gridTemplateColumns: narrow ? "1fr" : "260px 1fr 300px",gap:12,minHeight:480}}>
        {/* LEFT: GQ AI Chat */}
        <div className="card" style={{padding:0,display:"flex",flexDirection:"column",overflow:"hidden"}}>
          <div style={{padding:"12px 14px",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",gap:8}}>
            <div style={{width:22,height:22,borderRadius:6,background:"var(--primary-dim)",border:"1px solid rgba(217,70,239,0.3)",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <span style={{fontFamily:"'Inter Tight','Inter',sans-serif",fontSize:8,fontWeight:800,color:"#D946EF"}}>GQ</span>
            </div>
            <span style={{fontFamily:"var(--font-ui)",fontSize:13,fontWeight:600,color:"var(--ink)"}}>Strategy assistant</span>
          </div>
          <div ref={chatRef} style={{flex:1,overflowY:"auto",padding:12,display:"flex",flexDirection:"column"}}>
            {messages.map((m,i)=><ChatMsg key={i} role={m.role} text={m.text}/>)}
            {aiThinking && <ChatMsg role="ai" thinking/>}
          </div>
          {/* Suggested prompts */}
          <div style={{padding:"6px 12px",borderTop:"1px solid var(--border)",display:"flex",gap:4,flexWrap:"wrap"}}>
            {AI_PROMPTS.slice(0,3).map(p=>(
              <button key={p} onClick={()=>{setChatInput(p);}} style={{background:"rgba(255,255,255,0.03)",border:"1px solid var(--border)",borderRadius:6,padding:"3px 8px",cursor:"pointer",fontFamily:"var(--font-ui)",fontSize:10,color:"var(--muted)",whiteSpace:"nowrap"}}>{p}</button>
            ))}
          </div>
          <div style={{padding:"8px 12px 12px",borderTop:"1px solid var(--border)",display:"flex",gap:6}}>
            <input value={chatInput} onChange={e=>setChatInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&sendChat()}
              placeholder="Describe your strategy…" className="input" style={{flex:1,padding:"7px 10px",fontSize:12}}/>
            <button className="btn btn-primary btn-sm" onClick={sendChat} style={{padding:"6px 10px"}}><ArrowUp size={12}/></button>
          </div>
        </div>

        {/* CENTER: Flowchart Canvas */}
        <div style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:"var(--r-lg)",position:"relative",overflow:"hidden"}}>
          <svg style={{width:"100%",height:"100%",position:"absolute",inset:0}}>
            <defs>
              <pattern id="dotgrid2" width="20" height="20" patternUnits="userSpaceOnUse">
                <circle cx="1" cy="1" r=".8" fill="rgba(255,255,255,0.04)"/>
              </pattern>
              <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                <polygon points="0 0, 8 3, 0 6" fill="rgba(255,255,255,0.25)"/>
              </marker>
            </defs>
            <rect width="100%" height="100%" fill="url(#dotgrid2)"/>
            {/* Edges with arrows */}
            {edges.map((e,i)=>{
              const f=nodes.find(n=>n.id===e.from), t=nodes.find(n=>n.id===e.to);
              if(!f||!t) return null;
              const x1=f.x+140,y1=f.y+22,x2=t.x,y2=t.y+22;
              const mx=(x1+x2)/2;
              return <path key={i} d={`M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" markerEnd="url(#arrowhead)"/>;
            })}
            {/* Nodes */}
            {nodes.map(n=><FlowNode key={n.id} node={n} selected={selected===n.id} onClick={nd=>{setSelected(nd.id);setRightTab("params");}}/>)}
          </svg>
          <div style={{position:"absolute",bottom:10,left:14,display:"flex",gap:12,fontFamily:"var(--font-ui)",fontSize:10,color:"var(--subtle)"}}>
            <span>{nodes.length} nodes · {edges.length} connections</span>
          </div>
          <div style={{position:"absolute",bottom:10,right:14,display:"flex",gap:6}}>
            <button className="btn btn-ghost btn-sm" style={{padding:"3px 8px",fontSize:10}} onClick={()=>{setNodes(INIT_NODES);setSelected(null);onToast?.("Canvas reset");}}>Reset</button>
          </div>
        </div>

        {/* RIGHT: Code/Params/Import + Backtest */}
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div className="card" style={{padding:0,flex:showBT?undefined:1,overflow:"hidden",display:"flex",flexDirection:"column"}}>
            {/* Tabs */}
            <div style={{display:"flex",borderBottom:"1px solid var(--border)"}}>
              {[["code","Code"],["params","Params"],["import","Import"]].map(([id,label])=>(
                <button key={id} onClick={()=>setRightTab(id)} style={{
                  flex:1,background:"none",border:"none",borderBottom:`2px solid ${rightTab===id?"var(--accent)":"transparent"}`,
                  padding:"8px 0",cursor:"pointer",fontFamily:"var(--font-ui)",fontSize:11,fontWeight:rightTab===id?600:400,
                  color:rightTab===id?"var(--accent)":"var(--muted)",transition:"all .15s",
                }}>{label}</button>
              ))}
            </div>
            <div style={{flex:1,overflow:"auto",padding:12}}>
              {rightTab==="code" && (
                <textarea value={code} onChange={e=>setCode(e.target.value)} spellCheck={false}
                  style={{width:"100%",height:"100%",minHeight:200,background:"var(--bg-deep)",border:"1px solid var(--border)",borderRadius:6,padding:12,fontFamily:"var(--font-mono)",fontSize:12,color:"var(--body)",lineHeight:1.7,resize:"none",outline:"none"}}/>
              )}
              {rightTab==="params" && sel ? (
                <div style={{display:"flex",flexDirection:"column",gap:12}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                    <div style={{width:8,height:8,borderRadius:"50%",background:NODE_COLORS[sel.type]||"#D946EF"}}/>
                    <span style={{fontFamily:"var(--font-ui)",fontSize:14,fontWeight:600,color:"var(--ink)"}}>{sel.label}</span>
                    <span className="badge badge-muted" style={{fontSize:9}}>{sel.type}</span>
                  </div>
                  {Object.entries(sel.params).map(([k,v])=>(
                    <div key={k}>
                      <div style={{fontFamily:"var(--font-ui)",fontSize:10,color:"var(--subtle)",marginBottom:4}}>{k}</div>
                      <input className="input" defaultValue={v} style={{padding:"6px 10px",fontFamily:"var(--font-mono)",fontSize:12}}/>
                    </div>
                  ))}
                  <button className="btn btn-danger btn-sm" onClick={()=>{setNodes(p=>p.filter(n=>n.id!==selected));setSelected(null);}} style={{marginTop:8}}>
                    <Trash size={10}/> Remove node
                  </button>
                </div>
              ) : rightTab==="params" && (
                <div style={{fontFamily:"var(--font-ui)",fontSize:12,color:"var(--subtle)",padding:"20px 0",textAlign:"center"}}>Click a node on the canvas to edit parameters.</div>
              )}
              {rightTab==="import" && (
                <div style={{display:"flex",flexDirection:"column",gap:12}}>
                  <div style={{border:"2px dashed var(--border-strong)",borderRadius:8,padding:24,textAlign:"center",cursor:"pointer"}} onClick={()=>{const c=window.prompt("Paste your strategy code:");if(c){onToast?.("Parsing code…");setTimeout(()=>onToast?.("Code parsed — 4 blocks added"),600);}}}>
                    <Upload size={20} color="var(--muted)"/>
                    <div style={{fontFamily:"var(--font-ui)",fontSize:12,color:"var(--muted)",marginTop:8}}>Drop a .py or .js file, or click to paste</div>
                  </div>
                  <div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--subtle)",lineHeight:1.5}}>GQ AI will parse your code, extract indicators and logic, and populate the visual canvas automatically.</div>
                </div>
              )}
            </div>
          </div>

          {/* Inline Backtest Results */}
          {showBT && (
            <div className="card" style={{border:"1px solid rgba(17,255,153,0.15)"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <div style={{width:6,height:6,borderRadius:"50%",background:"var(--green)",boxShadow:"0 0 6px rgba(17,255,153,0.6)"}}/>
                  <span style={{fontFamily:"var(--font-ui)",fontSize:12,fontWeight:600,color:"var(--ink)"}}>Backtest results</span>
                </div>
                <button className="btn btn-ghost btn-sm" style={{padding:"2px 6px"}} onClick={()=>setShowBT(false)}><X size={10}/></button>
              </div>
              <Sparkline data={eqData} color="#11ff99" height={60}/>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginTop:10}}>
                {[{l:"Return",v:`+${btStats.ret}%`,c:"var(--green)"},{l:"Sharpe",v:btStats.sharpe,c:"#D946EF"},{l:"Max DD",v:`${btStats.maxDD}%`,c:"var(--red)"},{l:"Trades",v:btStats.trades}].map(s=>(
                  <div key={s.l} style={{padding:"6px 8px",background:"rgba(255,255,255,0.02)",borderRadius:6}}>
                    <div style={{fontFamily:"var(--font-ui)",fontSize:9,color:"var(--subtle)"}}>{s.l}</div>
                    <div style={{fontFamily:"var(--font-mono)",fontSize:13,color:s.c||"var(--ink)",fontWeight:500}}>{s.v}</div>
                  </div>
                ))}
              </div>
              <div style={{display:"flex",gap:6,marginTop:10}}>
                <button className="btn btn-primary btn-sm" style={{flex:1,fontSize:11}} onClick={() => {
                  const runId = btResult?.runId || window.GQ?.state?.getLastBacktestRunId?.();
                  if (runId && window.navigateTo) window.navigateTo('backtest', { run: runId });
                  else onNavigate?.('backtest');
                }}><BarChart2 size={10}/> Full report</button>
                <button className="btn btn-ghost btn-sm" style={{flex:1,fontSize:11}} onClick={()=>onNavigate?.("optimize")}><Cpu size={10}/> Optimize</button>
              </div>
            </div>
          )}
          {running && (
            <div className="card" style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:20}}>
              <RefreshCw size={14} color="#3b9eff" style={{animation:"spin 1s linear infinite"}}/>
              <span style={{fontFamily:"var(--font-ui)",fontSize:12,color:"var(--muted)"}}>Backtesting…</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
window.DesignerPage = DesignerPage;
